// Grammar Topics Data - Main Index
// Based on "Grammar 90 Groups (GPT5) 5 Examples Each.txt"
// Structured for Supabase migration with multi-language support
// Organized for 3 learning modes: Read, Study, Test

// Import all grammar groups
import { group1Data } from './grammarGroups/group1BasicPatterns.js';
import { group2Data } from './grammarGroups/group2TenseAspect.js';
import { group3Data } from './grammarGroups/group3ModalityMood.js';
import { group4Data } from './grammarGroups/group4VoiceComplements.js';
import { group5Data } from './grammarGroups/group5PhrasesNonFinites.js';
import { group6Data } from './grammarGroups/group6Clauses.js';
import { group7Data } from './grammarGroups/group7.js';
import { group8Data } from './grammarGroups/group8.js';
import { group9Data } from './grammarGroups/group9.js';
import { group10Data } from './grammarGroups/group10.js';
import { group11Data } from './grammarGroups/group11.js';
import { group12Data } from './grammarGroups/group12.js';
import { group13Data } from './grammarGroups/group13CohesionDiscourse.js';
import { group14Data } from './grammarGroups/group14.js';
import { group15Data } from './grammarGroups/group15.js';
import { group16Data } from './grammarGroups/group16.js';
import { group17Data } from './grammarGroups/group17.js';
import { group18Data } from './grammarGroups/group18.js';
import { group19Data } from './grammarGroups/group19.js';
import { group20Data } from './grammarGroups/group20.js';

export const grammarTopicsData = {
  // Page Overview
  overview: {
    id: "grammar_topics",
    title: {
      tc: "語法主題",
      sc: "语法主题", 
      en: "Grammar Topics"
    },
    subtitle: {
      tc: "掌握90個核心語法結構",
      sc: "掌握90个核心语法结构",
      en: "Master 90 Core Grammar Structures"
    },
    description: {
      tc: "系統性學習英語語法，從基礎句型到高級結構",
      sc: "系统性学习英语语法，从基础句型到高级结构",
      en: "Systematic English grammar learning from basic patterns to advanced structures"
    }
  },

  // Learning Modes
  learningModes: {
    read: {
      id: "read_mode",
      title: {
        tc: "閱讀模式",
        sc: "阅读模式",
        en: "Read Mode"
      },
      description: {
        tc: "閱讀語法結構和例句，理解基本概念",
        sc: "阅读语法结构和例句，理解基本概念",
        en: "Read grammar structures and examples to understand basic concepts"
      },
      icon: "📖"
    },
    study: {
      id: "study_mode", 
      title: {
        tc: "學習模式",
        sc: "学习模式",
        en: "Study Mode"
      },
      description: {
        tc: "深入學習語法規則和應用方法",
        sc: "深入学习语法规则和应用方法",
        en: "Deep dive into grammar rules and application methods"
      },
      icon: "🎓"
    },
    test: {
      id: "test_mode",
      title: {
        tc: "測試模式",
        sc: "测试模式", 
        en: "Test Mode"
      },
      description: {
        tc: "通過練習測試鞏固語法知識",
        sc: "通过练习测试巩固语法知识",
        en: "Consolidate grammar knowledge through practice tests"
      },
      icon: "📝"
    }
  },

  // Grammar Groups (20 main groups) - Imported from separate files
  grammarGroups: [
    group1Data, // 基本句型與語氣 Basics and Clause Types
    group2Data, // 時態與體 Tense and Aspect
    group3Data, // 語氣與情態 Modality and Mood
    group4Data, // 語態與補語 Voice and Complements
    group5Data, // 片語與非謂語 Phrases and Non-finites
    group6Data, // 從句：名詞/形容詞/副詞 Clauses: Noun/Relative/Adverbial
    group7Data, // 並列與信息結構 Coordination and Information Structure
    group8Data, // 比較與程度 Comparison and Degree
    group9Data, // 疑問與附加構式 Questions and Tags
    group10Data, // 名詞片語與修飾 Noun Phrases and Modification
    group11Data, // 固定搭配與片語動詞 Collocations and Phrasal Verbs
    group12Data, // 存在/虛主詞/外置 Existentials and Extraposition
    group13Data, // 信息銜接/語篇 Cohesion and Discourse
    group14Data, // 風格與正式度 Register and Formality
    group15Data, // 倒裝/強調變體 Advanced Inversion/Emphasis
    group16Data, // 間接引語與視角 Reported Speech and Perspective
    group17Data, // 條件與混合 Conditionals and Mixed Forms
    group18Data, // 數量/一致/代詞 Number, Agreement, Pronouns
    group19Data, // 高階組合 Advanced Combinations
    group20Data  // 語用與連貫 Pragmatics and Coherence
  ],

  // User Progress Tracking
  userProgress: {
    userId: "peter",
    currentGroup: "group_1",
    currentTopic: "statements",
    completedTopics: ["statements", "negatives"],
    studyModeProgress: {
      group_1: {
        statements: { completed: true, score: 85 },
        negatives: { completed: true, score: 92 }
      }
    },
    testModeProgress: {
      group_1: {
        statements: { attempts: 3, bestScore: 90 },
        negatives: { attempts: 2, bestScore: 88 }
      }
    }
  },

  // Navigation
  navigation: {
    back: {
      tc: "返回",
      sc: "返回",
      en: "Back"
    },
    next: {
      tc: "下一個",
      sc: "下一个", 
      en: "Next"
    },
    previous: {
      tc: "上一個",
      sc: "上一个",
      en: "Previous"
    },
    start: {
      tc: "開始學習",
      sc: "开始学习",
      en: "Start Learning"
    },
    continue: {
      tc: "繼續學習",
      sc: "继续学习",
      en: "Continue Learning"
    }
  },

  // Status Messages
  statusMessages: {
    loading: {
      tc: "載入中...",
      sc: "加载中...",
      en: "Loading..."
    },
    completed: {
      tc: "已完成",
      sc: "已完成",
      en: "Completed"
    },
    inProgress: {
      tc: "進行中",
      sc: "进行中",
      en: "In Progress"
    },
    notStarted: {
      tc: "未開始",
      sc: "未开始",
      en: "Not Started"
    }
  }
};

// Helper functions for data management
export const getGrammarGroupById = (groupId) => {
  return grammarTopicsData.grammarGroups.find(group => group.id === groupId);
};

export const getTopicById = (groupId, topicId) => {
  const group = getGrammarGroupById(groupId);
  return group?.topics.find(topic => topic.id === topicId);
};

export const getNextTopic = (currentGroupId, currentTopicId) => {
  const group = getGrammarGroupById(currentGroupId);
  const currentIndex = group?.topics.findIndex(topic => topic.id === currentTopicId);
  
  if (currentIndex !== -1 && currentIndex < group.topics.length - 1) {
    return group.topics[currentIndex + 1];
  }
  
  // Move to next group
  const currentGroupIndex = grammarTopicsData.grammarGroups.findIndex(g => g.id === currentGroupId);
  if (currentGroupIndex < grammarTopicsData.grammarGroups.length - 1) {
    const nextGroup = grammarTopicsData.grammarGroups[currentGroupIndex + 1];
    return nextGroup.topics[0];
  }
  
  return null;
};

export const getPreviousTopic = (currentGroupId, currentTopicId) => {
  const group = getGrammarGroupById(currentGroupId);
  const currentIndex = group?.topics.findIndex(topic => topic.id === currentTopicId);
  
  if (currentIndex > 0) {
    return group.topics[currentIndex - 1];
  }
  
  // Move to previous group
  const currentGroupIndex = grammarTopicsData.grammarGroups.findIndex(g => g.id === currentGroupId);
  if (currentGroupIndex > 0) {
    const prevGroup = grammarTopicsData.grammarGroups[currentGroupIndex - 1];
    return prevGroup.topics[prevGroup.topics.length - 1];
  }
  
  return null;
};

export const updateUserProgress = (userId, groupId, topicId, mode, score) => {
  // This would be replaced with actual Supabase calls
  console.log(`Updating progress for user ${userId}, group ${groupId}, topic ${topicId}, mode ${mode}, score ${score}`);
};

export const getTopicProgress = (userId, groupId, topicId) => {
  // This would be replaced with actual Supabase calls
  return {
    readMode: { completed: true },
    studyMode: { completed: false, score: null },
    testMode: { attempts: 0, bestScore: null }
  };
};
